/**
 * dart-balloons.js — Dart Balloons (GDD 6.5.2).
 *
 * A 5 × 4 wall of balloons and five darts. A pop is worth 50; three balloons of
 * one colour popped adjacent in a row add a 200 point combo bonus; one distinct
 * prize balloon is worth 500. Darts that miss stick in the backboard.
 *
 * Two details are worth knowing before changing anything here:
 *
 * 1. The colour layout comes from the seeded RNG but ALWAYS contains a forced
 *    horizontal run of three, and the prize balloon is placed outside it. A
 *    round in which the combo bonus is unreachable would be a worse round, and
 *    a layout that only sometimes contains a run would be an untestable one.
 *
 * 2. A pop is decided by the dart's TIP, not by its collider. The tip position
 *    is swept from the previous frame to this one and tested against every live
 *    balloon, so a fast dart cannot tunnel past one. That is also why the dart
 *    carries a small ball collider rather than the factory's capsule — the
 *    capsule's axis is perpendicular to the dart model, and the sweep does the
 *    precision work anyway.
 *
 * Every tuning number lives in constants.js DART_BALLOONS.
 *
 * @module stalls/dart-balloons
 */

import * as THREE from 'three';
import { DART_BALLOONS, HUB, EVENTS } from '../constants.js';
import { StallBase, registerStall } from './framework.js';
import * as factory from '../assets3d/factory.js';
import { createStallLight } from '../world/lighting.js';
import { bus } from '../core/events.js';
import { audio } from '../audio/index.js';
import { haptics } from '../core/haptics.js';
import { randRange, randInt, pick } from '../core/testmode.js';

const CFG = DART_BALLOONS;

/** Distance from the dart's origin to its point, along the model's −Z. */
const DART_TIP = 0.08;
/** The dart model's nose direction, for velocity-aligning it in flight. */
const NOSE = new THREE.Vector3(0, 0, -1);
const ZERO = { x: 0, y: 0, z: 0 };
/** One shared scrap of confetti geometry for every burst. */
const CONFETTI_GEO = new THREE.BoxGeometry(0.022, 0.03, 0.004);

class DartBalloons extends StallBase {
  constructor(ctx) {
    super(ctx);
    /** @type {Array<Array<object>>} grid[row][col] */ this.grid = [];
    /** @type {object[]} Flat list of balloon records. */ this.balloons = [];
    /** @type {object[]} One record per dart. */ this._darts = [];
    /** Combo windows already paid, keyed `row:startCol`. */ this._combos = new Set();
    this._popped = 0;
    this._settle = 0;

    /** @type {Array<{mesh: THREE.Mesh, vel: THREE.Vector3, t: number}>} */
    this._confetti = [];

    this._plotInverse = new THREE.Matrix4();
    this._v = new THREE.Vector3();
    this._tip = new THREE.Vector3();
    this._local = new THREE.Vector3();
    this._seg = new THREE.Vector3();
    this._rel = new THREE.Vector3();
    this._q = new THREE.Quaternion();
  }

  // ── Static build ────────────────────────────────────────────────────────

  onBuild() {
    this.plot.root.updateWorldMatrix(true, false);
    this._plotInverse.copy(this.plot.root.matrixWorld).invert();

    const width = HUB.boothWidth - 0.4;

    // The backboard darts stick in, and a floor for the ones that fall short.
    // Kept light: it fills most of the view, so a dark board drags the whole
    // booth under the suite's "is anything actually rendered" threshold.
    const board = new THREE.Mesh(
      new THREE.BoxGeometry(width, 2.6, 0.12),
      factory.mat(0x8d7aa8, { rough: 0.95 }),
    );
    board.receiveShadow = true;
    this.spawn(board, { x: 0, y: 1.6, z: CFG.boardZ + 0.06 }, {
      type: 'fixed', shape: 'box', halfExtents: { x: width / 2, y: 1.3, z: 0.06 },
      position: this.localToWorld(0, 1.6, CFG.boardZ + 0.06),
      friction: 0.9, restitution: 0.02,
    });

    const floor = new THREE.Mesh(
      new THREE.BoxGeometry(width, 0.1, 3.6),
      factory.mat(0x6a5a44, { rough: 1 }),
    );
    floor.receiveShadow = true;
    this.spawn(floor, { x: 0, y: -0.05, z: 1.7 }, {
      type: 'fixed', shape: 'box', halfExtents: { x: width / 2, y: 0.05, z: 1.8 },
      position: this.localToWorld(0, -0.05, 1.7),
      friction: 0.8, restitution: 0.1,
    });

    this.buildTray({ width: 0.86, y: 1.22, z: 0.55 });
    // Beside the wall rather than over it. Mounted above, the panel's top edge
    // sat within 10 cm of the frustum and its title clipped off the screen.
    this.buildScoreboard({ x: -1.55, y: 1.85, z: 2.98, width: 1.15, height: 0.78 });

    // In front of the wall, not above it: a light behind the balloons leaves
    // their faces — the part you throw at — in shadow.
    const light = createStallLight(0xffe0b0, 3.6, 14);
    light.position.set(0, 2.5, 1.2);
    this.root.add(light);
  }

  // ── Round setup ─────────────────────────────────────────────────────────

  setupRound() {
    this.setRoundFormat({ attempts: CFG.dartsPerRound });
    this.plot.root.updateWorldMatrix(true, false);
    this._plotInverse.copy(this.plot.root.matrixWorld).invert();

    this.grid = [];
    this.balloons = [];
    this._combos.clear();
    this._popped = 0;
    this._settle = 0;
    this._confetti = [];

    const layout = this._makeLayout();
    for (let r = 0; r < CFG.rows; r++) {
      this.grid.push([]);
      for (let c = 0; c < CFG.cols; c++) this.grid[r].push(this._addBalloon(r, c, layout[r][c]));
    }

    let flight = 0;
    this._darts = this.dispense(
      CFG.dartsPerRound,
      () => factory.createDart(CFG.colors[flight++ % CFG.colors.length].hex),
      {
        y: 1.30, z: 0.55, spacing: 0.15,
        desc: {
          shape: 'ball', radius: 0.012, mass: 0.022,
          friction: 0.5, restitution: 0.05, ccd: true, angularDamping: 0.9,
        },
      },
    ).map((handle) => ({
      handle, stuck: false, live: false, age: 0, prevTip: new THREE.Vector3(),
    }));
  }

  /**
   * Colour every cell, then overwrite one horizontal run so the combo bonus is
   * always reachable, and place the prize balloon clear of that run.
   * @returns {Array<Array<{colorKey: string, hex: number, prize: boolean}>>}
   * @private
   */
  _makeLayout() {
    const grid = [];
    for (let r = 0; r < CFG.rows; r++) {
      grid.push([]);
      for (let c = 0; c < CFG.cols; c++) {
        const col = pick(CFG.colors);
        grid[r].push({ colorKey: col.key, hex: col.hex, prize: false });
      }
    }

    const runRow = randInt(0, CFG.rows - 1);
    const runStart = randInt(0, CFG.cols - CFG.comboRun);
    const runColor = pick(CFG.colors);
    for (let i = 0; i < CFG.comboRun; i++) {
      grid[runRow][runStart + i] = { colorKey: runColor.key, hex: runColor.hex, prize: false };
    }

    let pr = 0;
    let pc = 0;
    do {
      pr = randInt(0, CFG.rows - 1);
      pc = randInt(0, CFG.cols - 1);
    } while (pr === runRow && pc >= runStart && pc < runStart + CFG.comboRun);
    grid[pr][pc] = { colorKey: 'prize', hex: CFG.prizeColor, prize: true };

    return grid;
  }

  /** @private */
  _addBalloon(row, col, def) {
    const mesh = factory.createBalloon(def.hex);
    if (def.prize) this._decoratePrize(mesh);

    const local = new THREE.Vector3(
      (col - (CFG.cols - 1) / 2) * CFG.colSpacing,
      CFG.baseY + row * CFG.rowSpacing,
      CFG.boardZ - CFG.balloonStandoff,
    );

    // Kinematic rather than the factory's fixed default: a kinematic mesh is
    // driven by its body, so `moveTarget` relocates the balloon you can see and
    // not just the one physics knows about.
    const handle = this.spawn(mesh, local, {
      type: 'kinematic', sensor: true, shape: 'ball', radius: mesh.userData.radius,
      position: this.localToWorld(local.x, local.y, local.z),
    });

    const balloon = {
      row, col, handle, local,
      colorKey: def.colorKey,
      prize: def.prize,
      radius: mesh.userData.radius * (def.prize ? 1.15 : 1),
      centre: handle.position(),
      popped: false,
    };
    this.balloons.push(balloon);
    this.registerTarget(handle, { id: `balloon-r${row}c${col}`, group: def.colorKey });
    return balloon;
  }

  /** A three-bar star so the prize balloon reads differently at a glance. */
  _decoratePrize(mesh) {
    mesh.scale.setScalar(1.15);
    const bar = new THREE.BoxGeometry(0.11, 0.016, 0.004);
    for (let i = 0; i < 3; i++) {
      const arm = new THREE.Mesh(bar, factory.mat(0xfff6d0, { flat: true }));
      arm.position.z = -mesh.userData.radius * 0.94;
      arm.rotation.z = (i / 3) * Math.PI;
      mesh.add(arm);
    }
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  onUpdate(dt) {
    this._updateDarts(dt);
    this._updateConfetti(dt);
    this._checkRoundEnd(dt);
  }

  /** @private */
  _updateDarts(dt) {
    const flying = this._darts.filter((d) => d.live && !d.stuck && !d.handle.removed);
    if (flying.length === 0) return;

    // Balloons only move when a test repositions one, but refreshing while a
    // dart is in the air keeps the sweep honest for whatever the body says.
    for (const b of this.balloons) {
      if (!b.popped && !b.handle.removed) {
        const t = b.handle.body.translation();
        b.centre.set(t.x, t.y, t.z);
      }
    }

    for (const dart of flying) {
      dart.age += dt;
      this._alignToFlight(dart.handle);
      this._tipWorld(dart.handle, this._tip);

      const hit = this._sweepBalloons(dart.prevTip, this._tip);
      if (hit) {
        this._pop(hit, this._tip);
        this._stick(dart);
      } else if (this._pastBoard(this._tip) || dart.age > CFG.dartLifetime) {
        this._stick(dart);
      }
      dart.prevTip.copy(this._tip);
    }
  }

  /** Darts weathervane: the nose follows the velocity, like a real dart. @private */
  _alignToFlight(handle) {
    const vel = handle.velocity();
    if (vel.lengthSq() < 0.25) return;
    handle.setRotation(this._q.setFromUnitVectors(NOSE, vel.normalize()));
    handle.setAngularVelocity(ZERO);
  }

  /** @private @returns {THREE.Vector3} `out`, the dart's point in world space. */
  _tipWorld(handle, out) {
    const p = handle.body.translation();
    const r = handle.body.rotation();
    return out.set(0, 0, -DART_TIP)
      .applyQuaternion(this._q.set(r.x, r.y, r.z, r.w))
      .add(this._v.set(p.x, p.y, p.z));
  }

  /**
   * Closest approach of the swept tip segment to each live balloon. Sweeping
   * rather than point-testing is what stops a fast dart tunnelling past one.
   * @private @returns {object|null} The balloon it touched.
   */
  _sweepBalloons(from, to) {
    const seg = this._seg.copy(to).sub(from);
    const lenSq = seg.lengthSq();
    for (const b of this.balloons) {
      if (b.popped || b.handle.removed) continue;
      const rel = this._rel.copy(b.centre).sub(from);
      const t = lenSq > 1e-9 ? THREE.MathUtils.clamp(rel.dot(seg) / lenSq, 0, 1) : 0;
      if (rel.addScaledVector(seg, -t).length() <= b.radius) return b;
    }
    return null;
  }

  /** @private */
  _pastBoard(worldTip) {
    return this._local.copy(worldTip).applyMatrix4(this._plotInverse).z >= CFG.boardZ;
  }

  /** @private */
  _stick(dart) {
    dart.stuck = true;
    if (dart.handle.removed) return;
    dart.handle.freeze();
    dart.handle.setKinematic(true);
    audio.play('dart-stick', { volume: 0.6 });
  }

  /** @private */
  _pop(balloon, at) {
    balloon.popped = true;
    balloon.handle.userData.scored = true;
    this._popped += 1;

    const prize = balloon.prize;
    this.addScore(prize ? CFG.prizePoints : CFG.standardPoints, {
      position: at.clone(),
      label: prize ? 'PRIZE BALLOON!' : 'Pop',
      color: prize ? '#cba6ff' : '#ffd166',
    });

    this._burst(balloon);
    audio.play('balloon-pop');
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'balloon-pop' });
    haptics.play('right', 'impact', { force: 10 });

    balloon.handle.remove();
    this._checkCombos();
  }

  /**
   * Pay 200 for every window of three same-colour balloons popped adjacent in a
   * row, once each (GDD 6.5.2).
   * @private
   */
  _checkCombos() {
    for (let r = 0; r < CFG.rows; r++) {
      for (let c = 0; c + CFG.comboRun <= CFG.cols; c++) {
        const key = `${r}:${c}`;
        if (this._combos.has(key)) continue;
        const first = this.grid[r][c];
        if (!first.popped || first.prize) continue;

        let matched = true;
        for (let i = 1; i < CFG.comboRun && matched; i++) {
          const cell = this.grid[r][c + i];
          matched = cell.popped && !cell.prize && cell.colorKey === first.colorKey;
        }
        if (!matched) continue;

        this._combos.add(key);
        const mid = this.grid[r][c + Math.floor(CFG.comboRun / 2)].local;
        this.addScore(CFG.comboBonus, {
          position: this.localToWorld(mid.x, mid.y + 0.16, mid.z),
          label: `${first.colorKey} run of ${CFG.comboRun}`,
          color: '#6fe7c4',
        });
        audio.play('prize-earned');
      }
    }
  }

  // ── Confetti ────────────────────────────────────────────────────────────

  /** @private */
  _burst(balloon) {
    for (let i = 0; i < CFG.confettiCount; i++) {
      const piece = new THREE.Mesh(CONFETTI_GEO, factory.mat(
        i % 3 === 0 ? 0xfff6d0 : balloon.handle.mesh?.userData?.color || 0xffffff,
        { flat: true },
      ));
      piece.position.copy(balloon.local);
      this.root.add(piece);
      // Cone of debris back toward the player, so the burst is visible.
      const dir = new THREE.Vector3(randRange(-1, 1), randRange(-0.4, 1), randRange(-1, -0.2));
      this._confetti.push({
        mesh: piece,
        vel: dir.normalize().multiplyScalar(CFG.confettiSpeed * randRange(0.5, 1.2)),
        t: 0,
      });
    }
  }

  /** @private */
  _updateConfetti(dt) {
    for (let i = this._confetti.length - 1; i >= 0; i--) {
      const p = this._confetti[i];
      p.t += dt;
      p.vel.y -= 8.5 * dt;
      p.mesh.position.addScaledVector(p.vel, dt);
      p.mesh.rotation.x += dt * 7;
      p.mesh.rotation.y += dt * 4.5;
      if (p.t < CFG.confettiLife) continue;
      this.root.remove(p.mesh);
      this._confetti.splice(i, 1);
    }
  }

  // ── Round shape ─────────────────────────────────────────────────────────

  /** @private */
  _onThrowableReleased(handle) {
    const dart = this._darts.find((d) => d.handle === handle);
    if (dart) {
      dart.live = true;
      dart.age = 0;
      this._tipWorld(handle, dart.prevTip);
    }
    super._onThrowableReleased(handle);
  }

  /** @private */
  _checkRoundEnd(dt) {
    if (!this.roundActive) return;
    if (this.balloons.every((b) => b.popped)) { this.endRound(); return; }
    if (this.attemptsLeft > 0) return;
    if (this._darts.some((d) => d.live && !d.stuck && !d.handle.removed)) { this._settle = 0; return; }

    this._settle += dt;
    if (this._settle >= CFG.settleTime) this.endRound();
  }

  /** Nudge throws toward the nearest balloon still on the wall (GDD 5.3). */
  findAimTarget(releasePos) {
    let best = null;
    let bestD = Infinity;
    for (const b of this.balloons) {
      if (b.popped || b.handle.removed) continue;
      const d = b.centre.distanceTo(releasePos);
      if (d < bestD) { bestD = d; best = b.centre; }
    }
    return best ? { position: best.clone() } : null;
  }

  /** Internals for `__cc.stalls.debug()`. @returns {object} */
  debugState() {
    return {
      popped: this._popped,
      combos: this._combos.size,
      standing: this.balloons.filter((b) => !b.popped).length,
      layout: this.grid.map((row) => row.map((c) => (c.prize ? 'prize' : c.colorKey))),
      darts: this._darts.map((d) => ({
        live: d.live,
        stuck: d.stuck,
        local: d.handle.removed
          ? null
          : this._local.copy(this._tipWorld(d.handle, this._tip))
            .applyMatrix4(this._plotInverse).toArray(),
      })),
    };
  }

  updateScoreboard() {
    if (!this.scoreboard) return;
    this.scoreboard.setLines([
      `Score   ${this.score}`,
      `Darts   ${this.attemptsLeft} / ${this.maxAttempts}`,
      `Popped  ${this._popped} / ${this.balloons.length}`,
      `Combos  ${this._combos.size}`,
    ]);
  }

  onTeardown() {
    for (const p of this._confetti) this.root.remove(p.mesh);
    this._confetti = [];
    this.grid = [];
    this.balloons = [];
    this._darts = [];
    this._combos.clear();
  }
}

registerStall({
  id: 'dart-balloons',
  name: 'Dart Balloons',
  cost: CFG.cost,
  create: (ctx) => new DartBalloons(ctx),
});

export { DartBalloons };
